import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const verifyCredentials = useCallback(async (creds) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${creds.username}:${creds.password}`)
        }
      });
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setCredentials(creds);
        setUserInfo(data);
      } else {
        sessionStorage.removeItem('adminAuth');
        setCredentials(null);
        setUserInfo(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Auth verification failed:', error);
      }
      sessionStorage.removeItem('adminAuth');
      setCredentials(null);
      setUserInfo(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for stored credentials
    const stored = sessionStorage.getItem('adminAuth');
    if (stored) {
      const creds = JSON.parse(stored);
      setCredentials(creds);
      verifyCredentials(creds);
    } else {
      setIsLoading(false);
    }
  }, [verifyCredentials]);

  const login = async (username, password) => {
    const creds = { username, password };
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });
      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem('adminAuth', JSON.stringify(creds));
        setCredentials(creds);
        setIsAuthenticated(true);
        setUserInfo(data);
        return { success: true };
      } else {
        return { success: false, error: 'Invalid credentials' };
      }
    } catch (error) {
      return { success: false, error: 'Connection failed' };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('adminAuth');
    setCredentials(null);
    setIsAuthenticated(false);
    setUserInfo(null);
  };

  const getAuthHeader = useCallback(() => {
    if (!credentials) return {};
    return {
      'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
    };
  }, [credentials]);

  const updateCredentials = (newPassword) => {
    if (credentials) {
      const newCreds = { ...credentials, password: newPassword };
      sessionStorage.setItem('adminAuth', JSON.stringify(newCreds));
      setCredentials(newCreds);
    }
  };

  return { isAuthenticated, isLoading, login, logout, getAuthHeader, userInfo, updateCredentials };
};

export const useAdminData = (endpoint, authHeader) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!authHeader || !authHeader.Authorization) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}`, {
        headers: authHeader
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint, authHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createItem = async (item) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}`, {
        method: 'POST',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
      if (response.ok) {
        await fetchData();
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.detail || 'Failed to create' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateItem = async (id, item) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}/${id}`, {
        method: 'PUT',
        headers: {
          ...authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
      if (response.ok) {
        await fetchData();
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.detail || 'Failed to update' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteItem = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: authHeader
      });
      if (response.ok) {
        await fetchData();
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.detail || 'Failed to delete' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { data, loading, error, refetch: fetchData, createItem, updateItem, deleteItem };
};
